import { useEffect, useRef, useState } from "react";
import type { CSSProperties, KeyboardEvent, MouseEvent, PointerEvent, ReactNode } from "react";

const HOLD_MS = 600;

const isActivationKey = (e: KeyboardEvent) => e.key === " " || e.key === "Enter";

/**
 * A button that only fires after being held for HOLD_MS, with a fill that grows while it is
 * held. Used for taking points away, so a kid grabbing the phone cannot dock points with a
 * stray tap. Letting go, or sliding off, before the fill completes cancels.
 */
export function HoldButton({
  onConfirm,
  onHoldStart,
  label,
  className,
  disabled = false,
  children,
}: {
  onConfirm: () => void;
  onHoldStart?: () => void;
  /** Accessible name, e.g. "Hold to remove 5". */
  label: string;
  className: string;
  disabled?: boolean;
  children: ReactNode;
}) {
  const [holding, setHolding] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  // True while Space or Enter is down, so the click a browser may still send for that key is
  // not mistaken for an assistive-technology activation and fired a second time.
  const keyDown = useRef(false);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  function start() {
    if (disabled || timer.current !== undefined) return;
    setHolding(true);
    onHoldStart?.();
    timer.current = window.setTimeout(() => {
      timer.current = undefined;
      setHolding(false);
      onConfirm();
    }, HOLD_MS);
  }

  function cancel() {
    window.clearTimeout(timer.current);
    timer.current = undefined;
    setHolding(false);
  }

  // Touch pointers stay captured by the element they went down on, so pointerleave only comes
  // on release; checking the position is how sliding a finger off cancels the hold.
  function onPointerMove(e: PointerEvent<HTMLButtonElement>) {
    if (timer.current === undefined) return;
    const r = e.currentTarget.getBoundingClientRect();
    if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) cancel();
  }

  // VoiceOver, Switch Control and similar cannot hold: they activate with a single click whose
  // detail is 0. A pointer click (detail 1+) is ignored, since pointers must hold. A click from
  // Space or Enter also has detail 0, so keyDown screens those out.
  function onClick(e: MouseEvent<HTMLButtonElement>) {
    if (disabled || e.detail !== 0 || keyDown.current) return;
    onConfirm();
  }

  return (
    <button
      type="button"
      className={`hold ${className}`}
      data-holding={holding || undefined}
      style={{ "--hold-ms": `${HOLD_MS}ms` } as CSSProperties}
      aria-label={label}
      disabled={disabled}
      onPointerDown={(e) => e.button === 0 && start()}
      onPointerMove={onPointerMove}
      onPointerUp={cancel}
      onPointerCancel={cancel}
      onPointerLeave={cancel}
      onClick={onClick}
      onKeyDown={(e) => {
        if (!isActivationKey(e)) return;
        // Every press, repeats included, so a held Enter cannot auto-repeat clicks.
        e.preventDefault();
        keyDown.current = true;
        if (!e.repeat) start();
      }}
      onKeyUp={(e) => {
        if (!isActivationKey(e)) return;
        e.preventDefault();
        cancel();
        // Space's click (if the browser still sends one) fires right after keyup in the same
        // task, so clear the flag only once that has passed.
        window.setTimeout(() => (keyDown.current = false), 0);
      }}
      onBlur={() => {
        cancel();
        keyDown.current = false;
      }}
      onContextMenu={(e) => e.preventDefault()}
    >
      <span className="hold__fill" aria-hidden="true" />
      <span className="hold__label">{children}</span>
    </button>
  );
}
